import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const BRAND_LOGO = '/brand/varotrades-logo.png';

/* ════════════════════════════════════════════════════════════════════
   ░ VarotradesSplashLoader ░

   Cinematic loading screen — two concentric orbits of OHLC candles
   revolving around the VAROTRADES logo. Uses CSS 3D perspective + tilt
   so the inner ring appears closer than the outer ring (parallax depth),
   and each ring counter-rotates so the scene feels alive.

   Pure CSS + Framer Motion; runs at 60 fps with zero canvas work.
   ════════════════════════════════════════════════════════════════════ */
const Candle = ({ bull, bodyH, wickT, wickB }) => {
  const colour = bull ? '#10b981' : '#ef4444';
  const glow = bull ? 'rgba(16,185,129,0.55)' : 'rgba(239,68,68,0.55)';
  return (
    <div className="flex flex-col items-center" style={{ filter: `drop-shadow(0 0 6px ${glow})` }}>
      <div style={{ width: 2, height: wickT, background: colour }} />
      <div style={{ width: 12, height: bodyH, background: colour, borderRadius: 1 }} />
      <div style={{ width: 2, height: wickB, background: colour }} />
    </div>
  );
};

const VarotradesSplashLoader = ({ message = 'تشغيل المحرّك…' }) => {
  // Deterministic candle generation so the layout stays consistent
  // across renders. The visual movement comes from the orbits, not
  // the candle shapes themselves.
  const innerCandles = useMemo(() => Array.from({ length: 10 }, (_, i) => {
    const seed = i * 13;
    return {
      angle: (i / 10) * 360,
      bull: (seed % 3) !== 0,
      bodyH: 22 + ((seed * 7) % 14),
      wickT: 5 + ((seed * 3) % 6),
      wickB: 5 + ((seed * 5) % 7),
    };
  }), []);

  const outerCandles = useMemo(() => Array.from({ length: 16 }, (_, i) => {
    const seed = i * 17 + 41;
    return {
      angle: (i / 16) * 360,
      bull: (seed % 4) !== 0,
      bodyH: 18 + ((seed * 5) % 18),
      wickT: 4 + ((seed * 2) % 7),
      wickB: 4 + ((seed * 3) % 8),
    };
  }), []);

  const INNER_R = 105;
  const OUTER_R = 175;

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        background:
          'radial-gradient(circle at 30% 25%, #fff7ed 0%, #fff1e3 45%, #fef8f0 100%)',
        perspective: '1100px',
      }}
      data-testid="varotrades-splash"
    >
      {/* Warm glow orbs */}
      <div className="absolute -top-32 left-1/4 w-[420px] h-[420px] bg-[#FF5C00]/12 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 right-1/4 w-[460px] h-[460px] bg-[#FFB766]/16 rounded-full blur-[140px] pointer-events-none" />

      <div
        className="relative"
        style={{
          width: 420,
          height: 420,
          transformStyle: 'preserve-3d',
          transform: 'rotateX(18deg)', // subtle camera tilt → faux 3D
        }}
      >
        {/* ─── OUTER ORBIT — large radius, slow CCW ─── */}
        <div
          className="absolute inset-0"
          style={{
            transformStyle: 'preserve-3d',
            animation: 'varo-orbit-ccw 22s linear infinite',
          }}
        >
          {outerCandles.map((c, i) => {
            const totalH = c.bodyH + c.wickT + c.wickB;
            return (
              <div
                key={`out-${i}`}
                className="absolute top-1/2 left-1/2"
                style={{
                  width: 12,
                  height: totalH,
                  marginLeft: -6,
                  marginTop: -totalH / 2,
                  transform: `rotate(${c.angle}deg) translateY(-${OUTER_R}px) rotate(${-c.angle}deg)`,
                  transformOrigin: '50% 50%',
                  opacity: 0.55,
                }}
              >
                <Candle bull={c.bull} bodyH={c.bodyH} wickT={c.wickT} wickB={c.wickB} />
              </div>
            );
          })}
        </div>

        {/* ─── INNER ORBIT — smaller radius, faster CW (counter-rotates) ─── */}
        <div
          className="absolute inset-0"
          style={{
            transformStyle: 'preserve-3d',
            animation: 'varo-orbit-cw 12s linear infinite',
          }}
        >
          {innerCandles.map((c, i) => {
            const totalH = c.bodyH + c.wickT + c.wickB;
            return (
              <div
                key={`in-${i}`}
                className="absolute top-1/2 left-1/2"
                style={{
                  width: 14,
                  height: totalH,
                  marginLeft: -7,
                  marginTop: -totalH / 2,
                  transform: `rotate(${c.angle}deg) translateY(-${INNER_R}px) rotate(${-c.angle}deg)`,
                  transformOrigin: '50% 50%',
                  opacity: 0.95,
                }}
              >
                <Candle bull={c.bull} bodyH={c.bodyH} wickT={c.wickT} wickB={c.wickB} />
              </div>
            );
          })}
        </div>

        {/* ─── CENTER LOGO (sits above both rings) ─── */}
        <div
          className="absolute top-1/2 left-1/2"
          style={{ width: 160, height: 160, marginLeft: -80, marginTop: -80 }}
        >
          {/* Pulse rings */}
          <motion.span
            className="absolute inset-0 rounded-full border-2 border-[#FF5C00]/60"
            animate={{ scale: [1, 1.55, 1], opacity: [0.65, 0, 0.65] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut' }}
          />
          <motion.span
            className="absolute inset-0 rounded-full border border-[#FF8B3D]/55"
            animate={{ scale: [1, 1.85, 1], opacity: [0.45, 0, 0.45] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut', delay: 0.45 }}
          />

          {/* Rotating speed-arc */}
          <motion.svg
            viewBox="0 0 100 100"
            className="absolute inset-0 w-full h-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          >
            <defs>
              <linearGradient id="splashSpeedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FF5C00" />
                <stop offset="60%" stopColor="#FF8B3D" />
                <stop offset="100%" stopColor="#FFB766" />
              </linearGradient>
            </defs>
            <circle
              cx="50" cy="50" r="46"
              fill="none"
              stroke="url(#splashSpeedGrad)"
              strokeWidth="2.5"
              strokeDasharray="220 80"
              strokeLinecap="round"
            />
          </motion.svg>

          {/* White disc + logo */}
          <motion.div
            className="absolute inset-4 rounded-full bg-white flex items-center justify-center overflow-hidden"
            animate={{
              boxShadow: [
                '0 0 30px rgba(255,92,0,0.40), 0 0 64px rgba(255,140,40,0.25)',
                '0 0 46px rgba(255,92,0,0.65), 0 0 100px rgba(255,140,40,0.45)',
                '0 0 30px rgba(255,92,0,0.40), 0 0 64px rgba(255,140,40,0.25)',
              ],
            }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.img
              src={BRAND_LOGO}
              alt="VAROTRADES"
              className="w-[80%] h-auto object-contain"
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </div>
      </div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="absolute bottom-20 left-0 right-0 text-center"
      >
        <p className="text-gray-700 text-sm uppercase tracking-[0.45em] font-black">
          {message}
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {[0, 0.15, 0.30].map((d, i) => (
            <motion.span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#FF6F1A]"
              animate={{ opacity: [0.25, 1, 0.25], scale: [0.8, 1.2, 0.8] }}
              transition={{ duration: 1.2, delay: d, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
        </div>
      </motion.div>

      {/* Keyframes — orbital rotations */}
      <style>{`
        @keyframes varo-orbit-ccw {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes varo-orbit-cw {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VarotradesSplashLoader;
